<script lang="ts">
import type { StudyMixCompare2SlideProps } from '$lib/components/study/slide-layout-types'
import StudySlideStatTileCard from '../cards/StudySlideStatTileCard.svelte'
import BarChart from '../charts/BarChart.svelte'
import StudySlide from '../StudySlide.svelte'
import StudySlideContent from '../StudySlideContent.svelte'

/**
 * Payer / mix compare (`s6b` ADC-style): left `.hl` / `.bd` + `.cc` horizontal `BarChart`; right
 * concentration hero + `StudySlideStatTileCard` (`payer` variant) grid + `.hb`. Trusted HTML fields
 * match `StudyMixCompare2SlideProps`.
 */
let props: StudyMixCompare2SlideProps = $props()

const headlineStyle = props.headlineStyle ?? 'margin-bottom:.82rem;'
const introStyle = props.introStyle ?? 'margin-bottom:.95rem;'
const leftCardStyle = props.leftCardStyle ?? 'padding:.85rem 1rem .9rem;'
const tcStyle = props.tcStyle ?? 'gap:2.1rem;align-items:start;'
const tcWeighted = props.tcWeighted ?? true
const payerGridStyle =
  props.payerGridStyle ?? 'display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.58rem;'

const barChartHeight = $derived(
  props.barChartHeight ?? `${Math.min(420, Math.max(220, props.payerRows.length * 44))}px`,
)

function rowPct(count: number): number {
  const t = props.totalCount
  return t > 0 ? (count / t) * 100 : 0
}

function tilePercentLabel(pct: number): string {
  if (props.formatTilePercent) return props.formatTilePercent(pct)
  return pct < 0.1 ? '~0.0%' : `${pct.toFixed(1)}%`
}

function tileFooter(count: number): string {
  return props.formatTileFooter?.(count) ?? `${props.formatCount(count)} claims`
}
</script>

<StudySlide
  id={props.id}
  active={props.active}
  extraBottom={props.extraBottom}
  background={props.background}
  slideClass={props.slideClass}
>
  <StudySlideContent
    maxWidth={props.maxWidth ?? '980px'}
    contentClass={props.contentClass}
    style={props.contentStyle}
  >
    <div class="ey">{@html props.eyebrow}</div>
    <div class="tc" class:w={tcWeighted} style={tcStyle}>
      <div>
        <div class="hl" style={headlineStyle}>{@html props.headline}</div>
        <p class="bd" style={introStyle}>{@html props.introHtml}</p>

        <div class="cc" style={leftCardStyle}>
          <div class="ct">{@html props.leftCardTitle}</div>
          <div class="cs">{@html props.leftCardSubtitle}</div>
          <BarChart
            active={props.active ?? false}
            height={barChartHeight}
            horizontal={true}
            labels={props.payerRows.map((r) => r.name)}
            datasets={[
              {
                data: props.payerRows.map((r) => r.count),
                backgroundColor: props.payerRows.map((r) => r.barColor),
                borderRadius: 4,
              },
            ]}
            yTickFormat={props.formatCount}
            tooltipFormat={(v) => {
              const pct = props.totalCount > 0 ? ((v / props.totalCount) * 100).toFixed(1) : '0'
              return ` ${props.formatCount(v)} claims (${pct}%)`
            }}
          />
        </div>
      </div>

      <div style="display:flex;flex-direction:column;gap:.72rem;">
        <div
          style="text-align:center;padding:1.2rem 1rem;background:rgba(17,160,165,.07);border:1px solid var(--gbr);border-radius:10px;"
        >
          <div
            style="font-size:.58rem;color:var(--mu);letter-spacing:.12em;text-transform:uppercase;margin-bottom:.4rem;"
          >
            {@html props.heroLabelHtml}
          </div>
          <div
            style="font-family:'DM Serif Display',serif;font-size:3rem;line-height:1;color:var(--g);margin-bottom:.25rem;"
          >
            {props.heroStat}
          </div>
          <div style="font-size:.7rem;color:var(--t2);line-height:1.5;">
            {@html props.heroCaptionHtml}
          </div>
        </div>

        <div style={payerGridStyle}>
          {#each props.payerRows as { name, count, tileStripeColor }}
            {@const p = rowPct(count)}
            <StudySlideStatTileCard
              variant="payer"
              stripeColor={tileStripeColor}
              value={tilePercentLabel(p)}
              label={name}
              footer={tileFooter(count)}
            />
          {/each}
        </div>

        <div class="hb">
          <p>
            {@html props.insightHtml}
          </p>
        </div>
      </div>
    </div>
  </StudySlideContent>
</StudySlide>
